import type { IconName } from '@customerio/pluma-icons';
import type { Source } from '@customerio/hydra-ui-api';
import { getPipelinesIntegrationImageSrcOrIcon, PIPELINES_INTEGRATIONS_IMAGE_URLS } from '@/features/integrations';
import { ACTIVITY_LOG_SOURCE } from '@/utils/activity-log-source-types';
import { parsePipelinesUserAgent } from '@/utils/pipelines-user-agent';
import { isEmptyString } from '@/utils/string';
import { generatePath } from '@/services/router/generate-path';

/**
 * How a data-index item's sources are shown: which mark stands for each one,
 * and how a long list of them collapses.
 *
 * A source is either a Data Pipelines integration, identified by the trailing
 * JSON on its user agent, or one of Customer.io's own write paths, identified
 * by `source.type`. The integration answer wins, because a pipelines write
 * arrives through the track API and would otherwise read as a plain API call.
 */

/** The mark and name shown for one source. */
export type SourceIconInfo = {
  icon?: IconName;
  imageSrc?: string;
  title: string;
};

export const SOURCES_TOOLTIP_CONTENT = 'Source data available from Aug 25, 2025.';

const SOURCE_TYPE_ICONS = new Map<string, SourceIconInfo>([
  [ACTIVITY_LOG_SOURCE.TrackAPI, { icon: 'cio-logo', title: 'Customer.io Track API' }],
  [ACTIVITY_LOG_SOURCE.TrackEUAPI, { icon: 'cio-logo', title: 'Customer.io Track API (EU)' }],
  [ACTIVITY_LOG_SOURCE.UI, { icon: 'cio-logo', title: 'Updated in dashboard' }],
  [ACTIVITY_LOG_SOURCE.UIEvent, { icon: 'cio-logo', title: 'User Interface Event' }],
  [ACTIVITY_LOG_SOURCE.Import, { icon: 'upload', title: 'CSV Import' }],
  [ACTIVITY_LOG_SOURCE.SQLImport, { icon: 'database', title: 'SQL Import' }],
  [ACTIVITY_LOG_SOURCE.FormIntegration, { icon: 'form', title: 'Integration: Form' }],
  [ACTIVITY_LOG_SOURCE.Webhook, { icon: 'webhook', title: 'Webhook' }],
  [ACTIVITY_LOG_SOURCE.AttributeUpdateAction, { icon: 'person', title: 'Attribute Update Action' }],
  [ACTIVITY_LOG_SOURCE.QueryCollection, { icon: 'database', title: 'Collection Query' }],
  [ACTIVITY_LOG_SOURCE.ObjectCampaignsAudience, { icon: 'filter', title: 'Audience Filter' }],
  [ACTIVITY_LOG_SOURCE.Action, { icon: 'person', title: 'Action' }],
  [ACTIVITY_LOG_SOURCE.Newsletter, { icon: 'newsletter', title: 'Newsletter' }],
  [ACTIVITY_LOG_SOURCE.Transactional, { icon: 'transactional-api', title: 'Transactional' }],
  [ACTIVITY_LOG_SOURCE.System, { icon: 'cio-logo', title: 'System' }],
  [ACTIVITY_LOG_SOURCE.SystemAutomaticDeletion, { icon: 'cio-logo', title: 'System Auto-deletion' }],
  [ACTIVITY_LOG_SOURCE.Content, { icon: 'file-csv', title: 'Content' }],
  [ACTIVITY_LOG_SOURCE.ListUnsubscribe, { icon: 'unsubscribe', title: 'List Unsubscribe' }],
  [ACTIVITY_LOG_SOURCE.Subscriptions, { icon: 'person', title: 'Subscription' }],
  [ACTIVITY_LOG_SOURCE.ProfileMerge, { icon: 'merge', title: 'Profile Merge' }],
  [ACTIVITY_LOG_SOURCE.LLMAction, { icon: 'sparkle', title: 'LLM Action' }],
]);

/**
 * Display names for the pipelines sources the integration catalog has no
 * artwork for, so they still read as a product rather than a slug. A source
 * the catalog does have artwork for never reaches this map.
 */
const INTEGRATION_SOURCE_TITLES = new Map<string, string>([
  ['facebook', 'Facebook'],
  ['typeform', 'Typeform'],
  ['jotform', 'Jotform'],
]);

const UNKNOWN_SOURCE: SourceIconInfo = { icon: 'import', title: 'Unknown Source' };

function integrationIconInfo(sourceType: string): SourceIconInfo | null {
  const fromCatalog = getPipelinesIntegrationImageSrcOrIcon(sourceType);
  if (fromCatalog.imageSrc != null || fromCatalog.icon != null) {
    return {
      ...fromCatalog,
      // The catalog names Customer.io's own pipelines source by its slug.
      title: fromCatalog.title === 'customerio' ? 'Customer.io Journeys API' : `Integration: ${fromCatalog.title}`,
    };
  }

  const title = INTEGRATION_SOURCE_TITLES.get(sourceType);
  if (title == null) {
    return null;
  }
  return { imageSrc: PIPELINES_INTEGRATIONS_IMAGE_URLS[sourceType], title: `Integration: ${title}` };
}

/** The mark and name to show for one source. */
export function getSourceIconInfo(source: Source): SourceIconInfo {
  const { sourceType } = parsePipelinesUserAgent(source.ua);

  if (!isEmptyString(sourceType)) {
    const integration = integrationIconInfo(sourceType);
    if (integration != null) {
      return integration;
    }
  }

  if (source.type != null) {
    return SOURCE_TYPE_ICONS.get(source.type) ?? UNKNOWN_SOURCE;
  }

  return UNKNOWN_SOURCE;
}

/**
 * Sources bucketed by `type`, each bucket in the order the sources arrived,
 * so a hundred writes from the same place render as one entry. A source with
 * no type is dropped: there is nothing to group it under.
 */
export function groupSourcesByType(sources: Source[]): Source[][] {
  return sources.reduce<Source[][]>((groups, source) => {
    if (source?.type == null) {
      return groups;
    }

    const existing = groups.find((group) => group[0]?.type === source.type);
    if (existing != null) {
      existing.push(source);
    } else {
      groups.push([source]);
    }
    return groups;
  }, []);
}

/**
 * Where a source's mark links: the import that wrote it, the automation
 * whose attribute-update action did, or the Data Pipelines source named on
 * its user agent. `null` when there is nowhere to go, and the mark is not a
 * link, as in the page this replaces (`data-source-icon/index.gts`
 * `sourceHref`, where `#` stands for nowhere).
 *
 * An automation in another workspace is linked by that workspace's id,
 * straight to its overview; one in this workspace by the automation alone.
 */
export function sourceHref(source: Source, workspaceId: number): string | null {
  if (source.type === ACTIVITY_LOG_SOURCE.Import && source.import_id != null) {
    return generatePath('/workspaces/:workspaceId/imports/:importId', {
      workspaceId: String(workspaceId),
      importId: String(source.import_id),
    });
  }

  if (source.type === ACTIVITY_LOG_SOURCE.AttributeUpdateAction && source.campaign_id != null) {
    const campaignId = String(source.campaign_id);
    return source.environment_id === workspaceId
      ? generatePath('/workspaces/:workspaceId/journeys/automations/:campaignId', {
          workspaceId: String(workspaceId),
          campaignId,
        })
      : generatePath('/workspaces/:workspaceId/journeys/automations/:campaignId/overview', {
          workspaceId: String(source.environment_id),
          campaignId,
        });
  }

  const { workspaceId: pipelinesWorkspaceId, sourceId } = parsePipelinesUserAgent(source.ua);
  if (
    pipelinesWorkspaceId == null ||
    sourceId == null ||
    !/^\d+/.test(pipelinesWorkspaceId) ||
    !/^\d+/.test(sourceId)
  ) {
    return null;
  }
  return generatePath('/workspaces/:workspaceId/pipelines/sources/:sourceId/data-in', {
    workspaceId: pipelinesWorkspaceId,
    sourceId,
  });
}
